import { Float32BufferAttribute, NormalBlending } from 'three';
import { BufferGeometry, Points, ShaderMaterial } from 'three';
import { intsBetween } from '../utils/ints-between';
const vertexShader = `
	uniform float pointMultiplier;
	attribute float scale;
	attribute float alpha;
 
	varying float alphaToFrag;
	varying vec4 colorToFrag;
 
	void main() {
		vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);

		gl_Position = projectionMatrix * mvPosition;
		gl_PointSize = pointMultiplier * 1500.0 * scale / gl_Position.w;
 
		colorToFrag = vec4( color, alpha );
	}
`;
const fragmentShader = `
	uniform sampler2D diffuseTexture;

	varying vec4 colorToFrag;

	void main() {
    	gl_FragColor = texture2D(diffuseTexture, gl_PointCoord) * vec4(colorToFrag);
	}
`;
export class ParticleSystem {
    constructor(settings) {
        this.settings = settings;
        this.geometry = new BufferGeometry();
        this.particles = [];
        this.settings.sizeSpread = this.settings.sizeSpread || 0;
        this.settings.positionSpread = this.settings.positionSpread || [0, 0, 0];
        this.settings.alpha = this.settings.alpha || 1;
        this.settings.color = this.settings.color || [1, 1, 1];
        this.material = new ShaderMaterial({
            uniforms: {
                diffuseTexture: { value: settings.texture },
                pointMultiplier: { value: settings.size },
            },
            vertexShader,
            fragmentShader,
            blending: NormalBlending,
            depthTest: true,
            depthWrite: false,
            transparent: true,
            vertexColors: true,
        });
        this.mesh = new Points(this.geometry, this.material);
        this.mesh.position.copy(settings.position);
        // Always pre-cook some steps.
        this.lastUpdateStep = -2;
        this.update(0);
    }
    spread(base, spread) {
        return base + (Math.random() * spread) - (spread / 2);
    }
    spawn(spawnStep) {
        this.particles.push({
            position: this.settings.positionSpread.map(spread => this.spread(0, spread)),
            scale: this.spread(1, this.settings.sizeSpread),
            alpha: this.settings.alpha,
            spawnStep,
            color: this.settings.color,
        });
    }
    updateSettings(changes) {
        for (const key of Object.keys(changes)) {
            this.settings[key] = changes[key];
        }
    }
    update(step) {
        // Die.
        this.particles = this.particles.filter(particle => step - particle.spawnStep < this.settings.particleLife);
        // Figure out how many particles to spawn.
        const from = this.lastUpdateStep * this.settings.emitsPerStep;
        const to = step * this.settings.emitsPerStep;
        for (const spawnStep of intsBetween(from, to)) {
            this.spawn(spawnStep / this.settings.emitsPerStep);
        }
        // Update.
        for (const particle of this.particles) {
            const updateAmount = step - Math.max(this.lastUpdateStep, particle.spawnStep);
            particle.position[1] += updateAmount * 0.1;
            const lifeCycle = (step - particle.spawnStep) / this.settings.particleLife;
            particle.alpha = lifeCycle > 0.5 ? (1 - lifeCycle) * 2 * this.settings.alpha : lifeCycle * 2 * this.settings.alpha;
        }
        this.geometry.setAttribute('position', new Float32BufferAttribute(this.particles.map(particle => particle.position).flat(), 3));
        this.geometry.setAttribute('scale', new Float32BufferAttribute(this.particles.map(particle => particle.scale).flat(), 1));
        this.geometry.setAttribute('alpha', new Float32BufferAttribute(this.particles.map(particle => particle.alpha).flat(), 1));
        this.geometry.setAttribute('color', new Float32BufferAttribute(this.particles.map(particle => particle.color).flat(), 3));
        this.geometry.attributes.position.needsUpdate = true;
        this.geometry.attributes.scale.needsUpdate = true;
        this.lastUpdateStep = step;
    }
}
